/**
 * "One Rally Before" del lado del portal — condicionar un reporte por lo que
 * pasó en el rally ANTERIOR.
 *
 * Es la contraparte de `packages/stats/src/rally-filter.ts` (sólo la parte
 * `previousRally`: el cruce por rotación/fase sigue siendo territorio del API,
 * ver DIVERGENCIA 8 en `test/parity-stats.test.ts`). Mismas reglas, copiadas
 * con la fuente a la vista y fijadas por el test de paridad:
 *
 *  - "rally anterior" = el último rally CERRADO **del mismo set**, en orden
 *    cronológico y pase o no pase él mismo el filtro (la pregunta es qué pasó
 *    justo antes en la cancha, no en el recorte);
 *  - el primer rally de cada set no tiene anterior y NO pasa; el descanso
 *    entre sets corta la cadena (el envión no sobrevive al cambio de lado);
 *  - sólo los rallies cerrados se filtran: los segmentos sin punto (el rally
 *    abierto del final, los `lineup_set`/`comment` entre rallies) pasan
 *    siempre y no actualizan la cadena;
 *  - `wonBy` y `touch` van en AND; dentro de `touch`, los campos presentes se
 *    exigen todos sobre UN MISMO toque.
 *
 * Además viven acá los PRESETS en lenguaje de DT ("tras error de recepción
 * propio", "tras punto de break rival"): cada uno es una función pura
 * (equipo de referencia → filtro), así la UI y el query del API salen de la
 * misma tabla.
 */
import type { TeamSide } from '@scout/core';
import { otherSide, type Evaluation, type Rally } from './log';

/** Toque a buscar en el rally anterior; campos presentes en AND. */
export interface PreviousTouchFilter {
  team?: TeamSide;
  player?: number;
  skill?: string;
  evaluation?: Evaluation;
}

/** Condición sobre el rally anterior; `wonBy` y `touch` en AND. */
export interface PreviousRallyFilter {
  wonBy?: TeamSide;
  touch?: PreviousTouchFilter;
}

export interface PreviousRallySelection {
  /** Rallies que pasan (los no cerrados siempre pasan), en orden. */
  rallies: Rally[];
  /** ids de `touch`/`point` de los rallies descartados. */
  excludedIds: ReadonlySet<string>;
}

/** ¿Algún toque de `prev` cumple TODOS los campos pedidos? */
function hasMatchingTouch(prev: Rally, cond: PreviousTouchFilter): boolean {
  return prev.touches.some((t) => {
    const p = t.payload;
    if (cond.team !== undefined && p.team !== cond.team) return false;
    if (cond.player !== undefined && p.player !== cond.player) return false;
    if (cond.skill !== undefined && p.skill !== cond.skill) return false;
    if (cond.evaluation !== undefined && p.evaluation !== cond.evaluation) return false;
    return true;
  });
}

/** Sin rally anterior (primer rally del set) no pasa: no hay qué condicionar. */
function passesPrevious(prev: Rally | null, filter: PreviousRallyFilter): boolean {
  if (prev === null) return false;
  if (filter.wonBy !== undefined && prev.winner !== filter.wonBy) return false;
  if (filter.touch !== undefined && !hasMatchingTouch(prev, filter.touch)) return false;
  return true;
}

/**
 * Aplica `previousRally` sobre los rallies ya partidos (y ya recortados por
 * sets). Con `undefined` no se recorre nada: mismo costo y resultado de
 * siempre.
 */
export function selectByPreviousRally(
  rallies: readonly Rally[],
  filter: PreviousRallyFilter | undefined,
): PreviousRallySelection {
  if (filter === undefined) return { rallies: [...rallies], excludedIds: new Set<string>() };

  const kept: Rally[] = [];
  const excludedIds = new Set<string>();
  let prevClosed: Rally | null = null;
  let prevSet: number | null = null;

  for (const rally of rallies) {
    if (rally.set !== prevSet) {
      prevSet = rally.set;
      prevClosed = null;
    }
    let ok = true;
    if (rally.closed && rally.winner !== null) {
      ok = passesPrevious(prevClosed, filter);
      prevClosed = rally;
    }
    if (ok) {
      kept.push(rally);
      continue;
    }
    for (const event of rally.events) {
      if (event.type === 'touch' || event.type === 'point') excludedIds.add(event.id);
    }
  }
  return { rallies: kept, excludedIds };
}

// ---------------------------------------------------------------------------
// Presets en lenguaje de DT
// ---------------------------------------------------------------------------

/**
 * Cada preset se lee desde un EQUIPO de referencia ("propio"); el filtro que
 * produce es a nivel rally, así que recorta las dos mitades del reporte a la
 * vez (los rallies que entran son los mismos para los dos equipos).
 */
export const PREVIOUS_RALLY_PRESETS = [
  'afterOwnPoint',
  'afterRivalPoint',
  'afterOwnBreak',
  'afterRivalBreak',
  'afterOwnReceptionError',
  'afterOwnAttackError',
  'afterOwnAce',
] as const;

export type PreviousRallyPresetId = (typeof PREVIOUS_RALLY_PRESETS)[number];

export function isPreviousRallyPresetId(value: string): value is PreviousRallyPresetId {
  return (PREVIOUS_RALLY_PRESETS as readonly string[]).includes(value);
}

/** El filtro concreto de un preset, visto desde `team`. */
export function previousRallyPreset(
  id: PreviousRallyPresetId,
  team: TeamSide,
): PreviousRallyFilter {
  const rival = otherSide(team);
  switch (id) {
    case 'afterOwnPoint':
      return { wonBy: team };
    case 'afterRivalPoint':
      return { wonBy: rival };
    // Break = sacó Y ganó ese rally: el toque de saque identifica quién sacaba
    // (primer saque del rally) y `wonBy` exige que además lo haya ganado.
    case 'afterOwnBreak':
      return { wonBy: team, touch: { team, skill: 'serve' } };
    case 'afterRivalBreak':
      return { wonBy: rival, touch: { team: rival, skill: 'serve' } };
    case 'afterOwnReceptionError':
      return { touch: { team, skill: 'reception', evaluation: '=' } };
    case 'afterOwnAttackError':
      return { touch: { team, skill: 'attack', evaluation: '=' } };
    // Un ace es punto directo, así que el toque ya implica el ganador.
    case 'afterOwnAce':
      return { touch: { team, skill: 'serve', evaluation: '#' } };
  }
}

/**
 * El filtro como parámetros de query (`prevWonBy`, `prevTouch*`) para
 * `GET /report` y `GET /clips` — el nombre de cada campo es contrato con
 * `apps/api/src/routes/clip-filter.ts`.
 */
export function previousRallyQuery(
  filter: PreviousRallyFilter | undefined,
): Record<string, string | number | undefined> {
  if (!filter) return {};
  return {
    prevWonBy: filter.wonBy,
    prevTouchTeam: filter.touch?.team,
    prevTouchPlayer: filter.touch?.player,
    prevTouchSkill: filter.touch?.skill,
    prevTouchEvaluation: filter.touch?.evaluation,
  };
}
